import approvalInboxOutboxService from "@services/approvalInboxOutbox/approvalInboxOutboxService"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import type { RequestProgressDto } from "@models/approvalProgress/approvalProgressModel"
import { action, makeAutoObservable, observable } from "mobx"

class ApprovalInboxOutboxStore {
  @observable isLoading!: boolean
  @observable inboxData!: PagedResultDto<any>
  @observable outboxData!: PagedResultDto<any>
  @observable processedData!: PagedResultDto<any>
  @observable inboxCount!: number
  @observable progressData: RequestProgressDto | null = null
  @observable progressLoading = false
  @observable progressError: string | null = null

  private progressCache: Map<number, RequestProgressDto> = new Map()

  constructor() {
    makeAutoObservable(this)

    this.isLoading = false
    this.inboxData = { items: [], totalCount: 0 }
    this.outboxData = { items: [], totalCount: 0 }
    this.processedData = { items: [], totalCount: 0 }
    this.inboxCount = 0
  }

  @action
  async getInbox(params: any) {
    this.isLoading = true
    const result = await approvalInboxOutboxService
      .getInbox(params)
      .finally(() => (this.isLoading = false))
    this.inboxData = result
  }

  @action
  async getOutbox(params: any) {
    this.isLoading = true
    const result = await approvalInboxOutboxService
      .getOutbox(params)
      .finally(() => (this.isLoading = false))
    this.outboxData = result
  }

  @action
  async getProcessed(params: any) {
    this.isLoading = true
    const result = await approvalInboxOutboxService
      .getProcessed(params)
      .finally(() => (this.isLoading = false))
    this.processedData = result
  }

  @action
  async getInboxCount() {
    const result = await approvalInboxOutboxService.getInboxCount()
    this.inboxCount = result?.totalCount ?? 0
  }

  @action
  async approve(requestId: number, comment?: string) {
    this.isLoading = true
    await approvalInboxOutboxService
      .approve({ requestId, comment })
      .finally(() => (this.isLoading = false))
    this.invalidateProgress(requestId)
  }

  /**
   * Duyệt MỘT phiếu trong loạt "duyệt hàng loạt": không bật `isLoading` (cờ đó bật spinner của cả
   * bảng Inbox, còn loạt đã có bảng tiến trình riêng trong modal) và không bật popup của từng
   * phiếu — modal tự tổng kết. Lỗi được ném lên để runner ghi vào đúng dòng.
   */
  async approveInBatch(
    requestId: number,
    comment?: string,
    expectedCurrentStep?: number
  ) {
    await approvalInboxOutboxService.approve(
      { requestId, comment, expectedCurrentStep },
      { silent: true }
    )
    this.invalidateProgress(requestId)
  }

  @action
  async reject(requestId: number, comment: string) {
    this.isLoading = true
    await approvalInboxOutboxService
      .reject({ requestId, comment })
      .finally(() => (this.isLoading = false))
    this.invalidateProgress(requestId)
  }

  @action
  async getRequestProgress(requestId: number) {
    const cached = this.progressCache.get(requestId)
    if (cached) {
      this.progressData = cached
      return cached
    }

    this.progressLoading = true
    this.progressError = null
    try {
      const data = await approvalInboxOutboxService.getRequestProgress(requestId)
      this.progressData = data
      this.progressCache.set(requestId, data)
      return data
    } catch (error: unknown) {
      this.progressError = error instanceof Error ? error.message : "Unknown error"
      throw error
    } finally {
      this.progressLoading = false
    }
  }

  @action
  invalidateProgress(requestId: number) {
    this.progressCache.delete(requestId)
    if (this.progressData?.id === requestId) {
      this.progressData = null
    }
  }
}

export default ApprovalInboxOutboxStore
